/**
 * LAWITY - Practice Management Service Layer
 * Firestore CRUD operations for Clients, Matters, and Billing entities
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  serverTimestamp,
  writeBatch,
  increment,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type {
  Client,
  ClientFormData,
  RelatedParty,
  Matter,
  TimeEntry,
  TimeEntryFormData,
  TimeEntryStatus,
  Expense,
  ExpenseFormData,
  ExpenseStatus,
  Invoice,
  InvoiceFormData,
  InvoiceStatus,
  Payment,
  PaymentMethod,
  PaymentStatus,
  TrustTransaction,
  TrustTransactionType,
  TrustAccount,
  AuditLog,
  AuditEntityType,
  AuditAction,
  FieldChange,
  ListQueryOptions,
  PaginatedResponse,
  FilterOptions,
} from '../types/practice';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate a unique ID with prefix
 */
function generateId(prefix: string): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `${prefix}_${timestamp}${random}`;
}

/**
 * Generate sequential number (e.g., CLT-000001)
 */
async function generateSequentialNumber(
  collectionName: string,
  prefix: string
): Promise<string> {
  const counterRef = doc(db, 'counters', collectionName);
  const counterDoc = await getDoc(counterRef);

  let nextNumber = 1;
  if (counterDoc.exists()) {
    nextNumber = (counterDoc.data().current || 0) + 1;
  }

  await setDoc(counterRef, { current: nextNumber }, { merge: true });

  return `${prefix}-${nextNumber.toString().padStart(6, '0')}`;
}

/**
 * Convert Firestore Timestamp to Date
 */
function timestampToDate(timestamp: Timestamp | Date | undefined): Date | undefined {
  if (!timestamp) return undefined;
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

/**
 * Prepare document data with audit fields
 */
function prepareCreateData<T extends Record<string, any>>(
  data: T,
  userId: string
): T & { createdAt: any; createdBy: string; updatedAt: any; updatedBy: string; version: number } {
  return {
    ...data,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    version: 1,
  };
}

/**
 * Prepare document data for update
 */
function prepareUpdateData<T extends Record<string, any>>(
  data: Partial<T>,
  userId: string
): Partial<T> & { updatedAt: any; updatedBy: string } {
  return {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };
}

/**
 * Build query constraints from filter options
 */
function buildQueryConstraints(
  filters?: FilterOptions,
  sortField: string = 'createdAt',
  sortDirection: 'asc' | 'desc' = 'desc'
): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];

  if (filters?.status?.length) {
    constraints.push(where('status', 'in', filters.status));
  }

  if (filters?.type?.length) {
    constraints.push(where('type', 'in', filters.type));
  }

  if (filters?.clientId) {
    constraints.push(where('clientId', '==', filters.clientId));
  }

  if (filters?.matterId) {
    constraints.push(where('matterId', '==', filters.matterId));
  }

  if (filters?.userId) {
    constraints.push(where('userId', '==', filters.userId));
  }

  if (filters?.dateFrom) {
    constraints.push(where('date', '>=', filters.dateFrom));
  }

  if (filters?.dateTo) {
    constraints.push(where('date', '<=', filters.dateTo));
  }

  // Add soft delete filter (exclude deleted items by default)
  constraints.push(where('deletedAt', '==', null));

  constraints.push(orderBy(sortField, sortDirection));

  return constraints;
}

// =============================================================================
// CLIENT SERVICE
// =============================================================================

export const clientService = {
  /**
   * Create a new client
   */
  async create(data: ClientFormData, userId: string): Promise<Client> {
    const id = generateId('clt');
    const clientNumber = await generateSequentialNumber('clients', 'CLT');

    const clientData = prepareCreateData(
      {
        id,
        clientNumber,
        ...data,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    await setDoc(doc(db, 'clients', id), clientData);

    // Create audit log
    await auditService.log({
      entityType: 'client',
      entityId: id,
      entityName: data.type === 'individual'
        ? `${data.firstName} ${data.lastName}`
        : data.entityName || '',
      action: 'create',
      userId,
    });

    return clientData as unknown as Client;
  },

  /**
   * Get client by ID
   */
  async getById(id: string): Promise<Client | null> {
    const docSnap = await getDoc(doc(db, 'clients', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
      deletedAt: timestampToDate(data.deletedAt),
    } as Client;
  },

  /**
   * Get paginated list of clients
   */
  async list(options?: ListQueryOptions): Promise<PaginatedResponse<Client>> {
    const pageSize = options?.pageSize || 25;
    const sortField = options?.sort?.field || 'createdAt';
    const sortDirection = options?.sort?.direction || 'desc';

    const constraints = buildQueryConstraints(options?.filters, sortField, sortDirection);
    constraints.push(limit(pageSize + 1));

    const q = query(collection(db, 'clients'), ...constraints);
    const snapshot = await getDocs(q);

    const clients: Client[] = [];
    snapshot.docs.slice(0, pageSize).forEach(doc => {
      const data = doc.data();
      clients.push({
        ...data,
        id: doc.id,
        createdAt: timestampToDate(data.createdAt),
        updatedAt: timestampToDate(data.updatedAt),
      } as Client);
    });

    return {
      data: clients,
      total: clients.length,
      page: options?.page || 1,
      pageSize,
      hasMore: snapshot.docs.length > pageSize,
    };
  },

  /**
   * Update client
   */
  async update(
    id: string,
    data: Partial<ClientFormData>,
    userId: string,
    oldData?: Client
  ): Promise<Client> {
    const updateData = prepareUpdateData(data, userId);

    // Increment version for optimistic locking
    await updateDoc(doc(db, 'clients', id), {
      ...updateData,
      version: increment(1),
    });

    // Create audit log with field changes
    if (oldData) {
      const changes = calculateFieldChanges(oldData, data);
      await auditService.log({
        entityType: 'client',
        entityId: id,
        entityName: data.type === 'individual'
          ? `${data.firstName || oldData.firstName} ${data.lastName || oldData.lastName}`
          : data.entityName || oldData.entityName || '',
        action: 'update',
        userId,
        changes,
      });
    }

    return (await this.getById(id))!;
  },

  /**
   * Soft delete client
   */
  async delete(id: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'clients', id), {
      deletedAt: serverTimestamp(),
      deletedBy: userId,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await auditService.log({
      entityType: 'client',
      entityId: id,
      action: 'delete',
      userId,
    });
  },

  /**
   * Restore soft-deleted client
   */
  async restore(id: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'clients', id), {
      deletedAt: null,
      deletedBy: null,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await auditService.log({
      entityType: 'client',
      entityId: id,
      action: 'restore',
      userId,
    });
  },

  /**
   * Search clients by name
   */
  async search(searchTerm: string, limit: number = 10): Promise<Client[]> {
    // Firestore doesn't support full-text search natively
    // For production, consider Algolia or ElasticSearch
    // This is a simple prefix search on name fields
    const q = query(
      collection(db, 'clients'),
      where('deletedAt', '==', null),
      orderBy('lastName'),
      where('lastName', '>=', searchTerm),
      where('lastName', '<=', searchTerm + '\uf8ff'),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
    } as Client));
  },
};

// =============================================================================
// RELATED PARTY SERVICE
// =============================================================================

export const relatedPartyService = {
  /**
   * Create related party
   */
  async create(
    clientId: string,
    data: Omit<RelatedParty, 'id' | keyof import('../types/practice').AuditFields>,
    userId: string
  ): Promise<RelatedParty> {
    const id = generateId('rp');

    const partyData = prepareCreateData(
      {
        id,
        clientId,
        ...data,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    await setDoc(doc(db, 'clients', clientId, 'relatedParties', id), partyData);

    return partyData as unknown as RelatedParty;
  },

  /**
   * Get related parties for a client
   */
  async getByClientId(clientId: string): Promise<RelatedParty[]> {
    const q = query(
      collection(db, 'clients', clientId, 'relatedParties'),
      where('deletedAt', '==', null),
      where('isActive', '==', true)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
    } as RelatedParty));
  },

  /**
   * Update related party
   */
  async update(
    clientId: string,
    partyId: string,
    data: Partial<RelatedParty>,
    userId: string
  ): Promise<void> {
    const updateData = prepareUpdateData(data, userId);
    await updateDoc(doc(db, 'clients', clientId, 'relatedParties', partyId), updateData);
  },

  /**
   * Delete related party (soft delete)
   */
  async delete(clientId: string, partyId: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'clients', clientId, 'relatedParties', partyId), {
      deletedAt: serverTimestamp(),
      deletedBy: userId,
      isActive: false,
    });
  },
};

// =============================================================================
// TIME ENTRY SERVICE
// =============================================================================

export const timeEntryService = {
  /**
   * Create time entry
   */
  async create(data: TimeEntryFormData, userId: string, userName?: string): Promise<TimeEntry> {
    const id = generateId('te');

    // Calculate amount
    const amount = data.hours * (data.rate || 0);

    const entryData = prepareCreateData(
      {
        id,
        ...data,
        userId,
        userName,
        amount,
        billed: false,
        status: 'draft' as TimeEntryStatus,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    await setDoc(doc(db, 'timeEntries', id), entryData);

    // Update matter unbilled time
    if (data.billable) {
      await updateDoc(doc(db, 'matters', data.matterId), {
        unbilledTime: increment(amount),
      });
    }

    await auditService.log({
      entityType: 'time_entry',
      entityId: id,
      action: 'create',
      userId,
    });

    return entryData as unknown as TimeEntry;
  },

  /**
   * Get time entry by ID
   */
  async getById(id: string): Promise<TimeEntry | null> {
    const docSnap = await getDoc(doc(db, 'timeEntries', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      date: timestampToDate(data.date),
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as TimeEntry;
  },

  /**
   * Get time entries for a matter
   */
  async getByMatterId(
    matterId: string,
    options?: { status?: TimeEntryStatus[]; billable?: boolean }
  ): Promise<TimeEntry[]> {
    const constraints: QueryConstraint[] = [
      where('matterId', '==', matterId),
      where('deletedAt', '==', null),
    ];

    if (options?.status?.length) {
      constraints.push(where('status', 'in', options.status));
    }

    if (options?.billable !== undefined) {
      constraints.push(where('billable', '==', options.billable));
    }

    constraints.push(orderBy('date', 'desc'));

    const q = query(collection(db, 'timeEntries'), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as TimeEntry));
  },

  /**
   * Get unbilled time entries for invoicing
   */
  async getUnbilledForClient(clientId: string): Promise<TimeEntry[]> {
    const q = query(
      collection(db, 'timeEntries'),
      where('clientId', '==', clientId),
      where('status', 'in', ['draft', 'submitted', 'approved']),
      where('billed', '==', false),
      where('billable', '==', true),
      where('deletedAt', '==', null),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as TimeEntry));
  },

  /**
   * Update time entry
   */
  async update(id: string, data: Partial<TimeEntryFormData>, userId: string): Promise<TimeEntry> {
    const current = await this.getById(id);
    if (!current) throw new Error('Time entry not found');

    // Prevent editing if already invoiced
    if (current.status === 'invoiced') {
      throw new Error('Cannot edit invoiced time entries');
    }

    // Recalculate amount if hours or rate changed
    const hours = data.hours ?? current.hours;
    const rate = data.rate ?? current.rate;
    const amount = hours * rate;

    const updateData = prepareUpdateData(
      { ...data, amount },
      userId
    );

    await updateDoc(doc(db, 'timeEntries', id), updateData);

    return (await this.getById(id))!;
  },

  /**
   * Submit time entry for approval
   */
  async submit(id: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'timeEntries', id), {
      status: 'submitted',
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  },

  /**
   * Approve time entry
   */
  async approve(id: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'timeEntries', id), {
      status: 'approved',
      approvedBy: userId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await auditService.log({
      entityType: 'time_entry',
      entityId: id,
      action: 'approve',
      userId,
    });
  },

  /**
   * Write off time entry
   */
  async writeOff(id: string, reason: string, userId: string): Promise<void> {
    const entry = await this.getById(id);
    if (!entry) throw new Error('Time entry not found');

    await updateDoc(doc(db, 'timeEntries', id), {
      status: 'written_off',
      writeOffReason: reason,
      writeOffBy: userId,
      writeOffAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    // Update matter unbilled time
    if (entry.billable && !entry.billed) {
      await updateDoc(doc(db, 'matters', entry.matterId), {
        unbilledTime: increment(-entry.amount),
      });
    }
  },

  /**
   * Delete time entry (soft delete)
   */
  async delete(id: string, userId: string): Promise<void> {
    const entry = await this.getById(id);
    if (!entry) return;

    if (entry.status === 'invoiced') {
      throw new Error('Cannot delete invoiced time entries');
    }

    await updateDoc(doc(db, 'timeEntries', id), {
      deletedAt: serverTimestamp(),
      deletedBy: userId,
    });

    // Update matter unbilled time
    if (entry.billable && !entry.billed) {
      await updateDoc(doc(db, 'matters', entry.matterId), {
        unbilledTime: increment(-entry.amount),
      });
    }
  },
};

// =============================================================================
// EXPENSE SERVICE
// =============================================================================

export const expenseService = {
  /**
   * Create expense
   */
  async create(data: ExpenseFormData, userId: string, userName?: string): Promise<Expense> {
    const id = generateId('exp');

    const expenseData = prepareCreateData(
      {
        id,
        ...data,
        userId,
        userName,
        billed: false,
        status: 'draft' as ExpenseStatus,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    await setDoc(doc(db, 'expenses', id), expenseData);

    // Update matter unbilled expenses
    if (data.billable) {
      await updateDoc(doc(db, 'matters', data.matterId), {
        unbilledExpenses: increment(data.amount),
      });
    }

    await auditService.log({
      entityType: 'expense',
      entityId: id,
      action: 'create',
      userId,
    });

    return expenseData as unknown as Expense;
  },

  /**
   * Get expense by ID
   */
  async getById(id: string): Promise<Expense | null> {
    const docSnap = await getDoc(doc(db, 'expenses', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      date: timestampToDate(data.date),
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Expense;
  },

  /**
   * Get expenses for a matter
   */
  async getByMatterId(matterId: string): Promise<Expense[]> {
    const q = query(
      collection(db, 'expenses'),
      where('matterId', '==', matterId),
      where('deletedAt', '==', null),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as Expense));
  },

  /**
   * Get unbilled expenses for invoicing
   */
  async getUnbilledForClient(clientId: string): Promise<Expense[]> {
    const q = query(
      collection(db, 'expenses'),
      where('clientId', '==', clientId),
      where('status', 'in', ['draft', 'submitted', 'approved']),
      where('billed', '==', false),
      where('billable', '==', true),
      where('deletedAt', '==', null),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as Expense));
  },

  /**
   * Update expense
   */
  async update(id: string, data: Partial<ExpenseFormData>, userId: string): Promise<Expense> {
    const current = await this.getById(id);
    if (!current) throw new Error('Expense not found');

    if (current.status === 'invoiced') {
      throw new Error('Cannot edit invoiced expenses');
    }

    const updateData = prepareUpdateData(data, userId);
    await updateDoc(doc(db, 'expenses', id), updateData);

    return (await this.getById(id))!;
  },

  /**
   * Approve expense
   */
  async approve(id: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'expenses', id), {
      status: 'approved',
      approvedBy: userId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await auditService.log({
      entityType: 'expense',
      entityId: id,
      action: 'approve',
      userId,
    });
  },

  /**
   * Delete expense (soft delete)
   */
  async delete(id: string, userId: string): Promise<void> {
    const expense = await this.getById(id);
    if (!expense) return;

    if (expense.status === 'invoiced') {
      throw new Error('Cannot delete invoiced expenses');
    }

    await updateDoc(doc(db, 'expenses', id), {
      deletedAt: serverTimestamp(),
      deletedBy: userId,
    });

    // Update matter unbilled expenses
    if (expense.billable && !expense.billed) {
      await updateDoc(doc(db, 'matters', expense.matterId), {
        unbilledExpenses: increment(-expense.amount),
      });
    }
  },
};

// =============================================================================
// INVOICE SERVICE
// =============================================================================

export const invoiceService = {
  /**
   * Create invoice
   */
  async create(data: InvoiceFormData, userId: string): Promise<Invoice> {
    const id = generateId('inv');
    const invoiceNumber = await generateSequentialNumber('invoices', 'INV');

    // Get client and matter names
    const client = await clientService.getById(data.clientId);

    // Calculate totals
    let subtotalTime = 0;
    let subtotalExpenses = 0;
    let subtotalFlatFees = 0;

    // Sum time entries
    for (const teId of data.timeEntryIds) {
      const te = await timeEntryService.getById(teId);
      if (te) subtotalTime += te.amount;
    }

    // Sum expenses
    for (const expId of data.expenseIds) {
      const exp = await expenseService.getById(expId);
      if (exp) subtotalExpenses += exp.amount;
    }

    // Sum flat fees
    if (data.flatFeeItems) {
      subtotalFlatFees = data.flatFeeItems.reduce((sum, item) => sum + item.total, 0);
    }

    const subtotal = subtotalTime + subtotalExpenses + subtotalFlatFees;

    // Calculate discount
    let discountAmount = 0;
    if (data.discountType === 'percentage' && data.discountValue) {
      discountAmount = subtotal * (data.discountValue / 100);
    } else if (data.discountType === 'fixed' && data.discountValue) {
      discountAmount = data.discountValue;
    }

    // Calculate tax
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = data.taxRate ? taxableAmount * (data.taxRate / 100) : 0;

    // Apply trust balance if requested
    let trustApplied = 0;
    if (data.applyTrustBalance && client?.trustBalance) {
      trustApplied = Math.min(client.trustBalance, taxableAmount + taxAmount);
    }

    const totalAmount = taxableAmount + taxAmount;
    const balanceDue = totalAmount - trustApplied;

    const invoiceData = prepareCreateData(
      {
        id,
        invoiceNumber,
        clientId: data.clientId,
        clientName: client?.type === 'individual'
          ? `${client.firstName} ${client.lastName}`
          : client?.entityName,
        matterId: data.matterId,
        issueDate: new Date(data.issueDate),
        dueDate: new Date(data.dueDate),
        status: 'draft' as InvoiceStatus,
        timeEntryIds: data.timeEntryIds,
        expenseIds: data.expenseIds,
        flatFeeItems: data.flatFeeItems || [],
        subtotalTime,
        subtotalExpenses,
        subtotalFlatFees,
        subtotal,
        discountType: data.discountType,
        discountValue: data.discountValue,
        discountAmount,
        taxRate: data.taxRate,
        taxAmount,
        totalAmount,
        amountPaid: 0,
        balanceDue,
        trustApplied,
        headerNote: data.headerNote,
        footerNote: data.footerNote,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    await setDoc(doc(db, 'invoices', id), invoiceData);

    await auditService.log({
      entityType: 'invoice',
      entityId: id,
      entityName: invoiceNumber,
      action: 'create',
      userId,
    });

    return invoiceData as unknown as Invoice;
  },

  /**
   * Get invoice by ID
   */
  async getById(id: string): Promise<Invoice | null> {
    const docSnap = await getDoc(doc(db, 'invoices', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      issueDate: timestampToDate(data.issueDate),
      dueDate: timestampToDate(data.dueDate),
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Invoice;
  },

  /**
   * Get invoices for a client
   */
  async getByClientId(clientId: string): Promise<Invoice[]> {
    const q = query(
      collection(db, 'invoices'),
      where('clientId', '==', clientId),
      where('deletedAt', '==', null),
      orderBy('issueDate', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      issueDate: timestampToDate(doc.data().issueDate),
      dueDate: timestampToDate(doc.data().dueDate),
    } as Invoice));
  },

  /**
   * Approve invoice
   */
  async approve(id: string, userId: string): Promise<void> {
    const invoice = await this.getById(id);
    if (!invoice) throw new Error('Invoice not found');

    const batch = writeBatch(db);

    // Update invoice status
    batch.update(doc(db, 'invoices', id), {
      status: 'approved',
      approvedBy: userId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    // Mark time entries as invoiced
    for (const teId of invoice.timeEntryIds) {
      batch.update(doc(db, 'timeEntries', teId), {
        status: 'invoiced',
        billed: true,
        invoiceId: id,
        invoiceDate: serverTimestamp(),
      });
    }

    // Mark expenses as invoiced
    for (const expId of invoice.expenseIds) {
      batch.update(doc(db, 'expenses', expId), {
        status: 'invoiced',
        billed: true,
        invoiceId: id,
      });
    }

    await batch.commit();

    await auditService.log({
      entityType: 'invoice',
      entityId: id,
      entityName: invoice.invoiceNumber,
      action: 'approve',
      userId,
    });
  },

  /**
   * Send invoice
   */
  async send(id: string, method: 'email' | 'mail' | 'portal', userId: string): Promise<void> {
    const invoice = await this.getById(id);
    if (!invoice) throw new Error('Invoice not found');

    await updateDoc(doc(db, 'invoices', id), {
      status: invoice.status === 'approved' ? 'sent' : invoice.status,
      sentAt: serverTimestamp(),
      sentMethod: method,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await auditService.log({
      entityType: 'invoice',
      entityId: id,
      entityName: invoice.invoiceNumber,
      action: 'send',
      userId,
      metadata: { method },
    });
  },

  /**
   * Void invoice
   */
  async void(id: string, reason: string, userId: string): Promise<void> {
    const invoice = await this.getById(id);
    if (!invoice) throw new Error('Invoice not found');

    const batch = writeBatch(db);

    // Update invoice
    batch.update(doc(db, 'invoices', id), {
      status: 'void',
      voidedBy: userId,
      voidedAt: serverTimestamp(),
      voidReason: reason,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    // Reverse time entry status back to approved
    for (const teId of invoice.timeEntryIds) {
      batch.update(doc(db, 'timeEntries', teId), {
        status: 'approved',
        billed: false,
        invoiceId: null,
        invoiceDate: null,
      });
    }

    // Reverse expense status back to approved
    for (const expId of invoice.expenseIds) {
      batch.update(doc(db, 'expenses', expId), {
        status: 'approved',
        billed: false,
        invoiceId: null,
      });
    }

    await batch.commit();

    await auditService.log({
      entityType: 'invoice',
      entityId: id,
      entityName: invoice.invoiceNumber,
      action: 'void',
      userId,
      metadata: { reason },
    });
  },

  /**
   * Get outstanding invoices (for AR aging)
   */
  async getOutstanding(): Promise<Invoice[]> {
    const q = query(
      collection(db, 'invoices'),
      where('status', 'in', ['sent', 'viewed', 'partial', 'overdue']),
      where('balanceDue', '>', 0),
      where('deletedAt', '==', null),
      orderBy('balanceDue', 'desc'),
      orderBy('dueDate', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      issueDate: timestampToDate(doc.data().issueDate),
      dueDate: timestampToDate(doc.data().dueDate),
    } as Invoice));
  },
};

// =============================================================================
// PAYMENT SERVICE
// =============================================================================

export const paymentService = {
  /**
   * Record payment
   */
  async create(
    invoiceId: string,
    amount: number,
    method: PaymentMethod,
    data: {
      reference?: string;
      notes?: string;
      depositAccountType?: 'operating' | 'trust';
    },
    userId: string
  ): Promise<Payment> {
    const id = generateId('pmt');
    const invoice = await invoiceService.getById(invoiceId);
    if (!invoice) throw new Error('Invoice not found');

    const paymentData = prepareCreateData(
      {
        id,
        invoiceId,
        invoiceNumber: invoice.invoiceNumber,
        clientId: invoice.clientId,
        clientName: invoice.clientName,
        date: new Date(),
        amount,
        method,
        reference: data.reference,
        notes: data.notes,
        status: 'completed' as PaymentStatus,
        depositAccountType: data.depositAccountType || 'operating',
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    const batch = writeBatch(db);

    // Create payment
    batch.set(doc(db, 'payments', id), paymentData);

    // Update invoice
    const newAmountPaid = invoice.amountPaid + amount;
    const newBalanceDue = invoice.totalAmount - newAmountPaid;
    const newStatus: InvoiceStatus =
      newBalanceDue <= 0 ? 'paid' :
      newAmountPaid > 0 ? 'partial' : invoice.status;

    batch.update(doc(db, 'invoices', invoiceId), {
      amountPaid: newAmountPaid,
      balanceDue: newBalanceDue,
      status: newStatus,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await batch.commit();

    await auditService.log({
      entityType: 'payment',
      entityId: id,
      action: 'create',
      userId,
      metadata: { invoiceId, amount, method },
    });

    return paymentData as unknown as Payment;
  },

  /**
   * Get payments for an invoice
   */
  async getByInvoiceId(invoiceId: string): Promise<Payment[]> {
    const q = query(
      collection(db, 'payments'),
      where('invoiceId', '==', invoiceId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as Payment));
  },

  /**
   * Get payments for a client
   */
  async getByClientId(clientId: string): Promise<Payment[]> {
    const q = query(
      collection(db, 'payments'),
      where('clientId', '==', clientId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as Payment));
  },

  /**
   * Refund payment
   */
  async refund(id: string, reason: string, userId: string): Promise<void> {
    const payment = await getDoc(doc(db, 'payments', id));
    if (!payment.exists()) throw new Error('Payment not found');

    const paymentData = payment.data();
    const invoice = await invoiceService.getById(paymentData.invoiceId);

    const batch = writeBatch(db);

    // Update payment status
    batch.update(doc(db, 'payments', id), {
      status: 'refunded',
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    // Reverse invoice amounts
    if (invoice) {
      const newAmountPaid = invoice.amountPaid - paymentData.amount;
      const newBalanceDue = invoice.totalAmount - newAmountPaid;

      batch.update(doc(db, 'invoices', paymentData.invoiceId), {
        amountPaid: newAmountPaid,
        balanceDue: newBalanceDue,
        status: newBalanceDue > 0 ? 'sent' : 'paid',
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });
    }

    await batch.commit();

    await auditService.log({
      entityType: 'payment',
      entityId: id,
      action: 'void',
      userId,
      metadata: { reason },
    });
  },
};

// =============================================================================
// TRUST ACCOUNT SERVICE
// =============================================================================

export const trustService = {
  /**
   * Create trust account for client
   */
  async createAccount(clientId: string, userId: string): Promise<TrustAccount> {
    const id = generateId('ta');
    const accountNumber = await generateSequentialNumber('trustAccounts', 'TRUST');

    const client = await clientService.getById(clientId);

    const accountData = {
      id,
      clientId,
      clientName: client?.type === 'individual'
        ? `${client.firstName} ${client.lastName}`
        : client?.entityName,
      accountNumber,
      balance: 0,
      status: 'active' as const,
      createdAt: serverTimestamp(),
      createdBy: userId,
    };

    const batch = writeBatch(db);

    batch.set(doc(db, 'trustAccounts', id), accountData);

    // Link to client
    batch.update(doc(db, 'clients', clientId), {
      trustAccountId: id,
      trustBalance: 0,
    });

    await batch.commit();

    return accountData as unknown as TrustAccount;
  },

  /**
   * Get trust account for client
   */
  async getByClientId(clientId: string): Promise<TrustAccount | null> {
    const q = query(
      collection(db, 'trustAccounts'),
      where('clientId', '==', clientId),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    return {
      ...doc.data(),
      id: doc.id,
    } as TrustAccount;
  },

  /**
   * Record trust deposit
   */
  async deposit(
    clientId: string,
    amount: number,
    description: string,
    payorPayee: string,
    reference: string,
    userId: string,
    matterId?: string
  ): Promise<TrustTransaction> {
    const account = await this.getByClientId(clientId);
    if (!account) throw new Error('Trust account not found');

    const id = generateId('tt');
    const newBalance = account.balance + amount;

    const transactionData = prepareCreateData(
      {
        id,
        clientId,
        matterId,
        date: new Date(),
        type: 'deposit' as TrustTransactionType,
        amount,
        runningBalance: newBalance,
        description,
        payorPayee,
        reference,
        reconciled: false,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    const batch = writeBatch(db);

    batch.set(doc(db, 'trustTransactions', id), transactionData);

    // Update account balance
    batch.update(doc(db, 'trustAccounts', account.id), {
      balance: newBalance,
      lastActivityDate: serverTimestamp(),
    });

    // Update client trust balance
    batch.update(doc(db, 'clients', clientId), {
      trustBalance: newBalance,
    });

    await batch.commit();

    await auditService.log({
      entityType: 'trust_transaction',
      entityId: id,
      action: 'create',
      userId,
      metadata: { type: 'deposit', amount },
    });

    return transactionData as unknown as TrustTransaction;
  },

  /**
   * Record trust disbursement
   */
  async disburse(
    clientId: string,
    amount: number,
    description: string,
    payorPayee: string,
    reference: string,
    userId: string,
    matterId?: string,
    invoiceId?: string
  ): Promise<TrustTransaction> {
    const account = await this.getByClientId(clientId);
    if (!account) throw new Error('Trust account not found');

    if (account.balance < amount) {
      throw new Error('Insufficient trust balance');
    }

    const id = generateId('tt');
    const newBalance = account.balance - amount;

    const transactionData = prepareCreateData(
      {
        id,
        clientId,
        matterId,
        date: new Date(),
        type: 'disbursement' as TrustTransactionType,
        amount: -amount,
        runningBalance: newBalance,
        description,
        payorPayee,
        reference,
        invoiceId,
        reconciled: false,
        deletedAt: null,
        deletedBy: null,
      },
      userId
    );

    const batch = writeBatch(db);

    batch.set(doc(db, 'trustTransactions', id), transactionData);

    batch.update(doc(db, 'trustAccounts', account.id), {
      balance: newBalance,
      lastActivityDate: serverTimestamp(),
    });

    batch.update(doc(db, 'clients', clientId), {
      trustBalance: newBalance,
    });

    await batch.commit();

    await auditService.log({
      entityType: 'trust_transaction',
      entityId: id,
      action: 'create',
      userId,
      metadata: { type: 'disbursement', amount },
    });

    return transactionData as unknown as TrustTransaction;
  },

  /**
   * Transfer from trust to operating (for invoice payment)
   */
  async transferToOperating(
    clientId: string,
    invoiceId: string,
    amount: number,
    userId: string
  ): Promise<TrustTransaction> {
    const invoice = await invoiceService.getById(invoiceId);
    if (!invoice) throw new Error('Invoice not found');

    // Create trust disbursement
    const transaction = await this.disburse(
      clientId,
      amount,
      `Payment for Invoice ${invoice.invoiceNumber}`,
      'Operating Account',
      `INV-${invoice.invoiceNumber}`,
      userId,
      invoice.matterId,
      invoiceId
    );

    // Record payment on invoice
    await paymentService.create(
      invoiceId,
      amount,
      'trust_transfer',
      {
        reference: transaction.id,
        notes: 'Transfer from trust account',
        depositAccountType: 'operating',
      },
      userId
    );

    return transaction;
  },

  /**
   * Get trust transactions for a client
   */
  async getTransactions(clientId: string): Promise<TrustTransaction[]> {
    const q = query(
      collection(db, 'trustTransactions'),
      where('clientId', '==', clientId),
      where('deletedAt', '==', null),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as TrustTransaction));
  },

  /**
   * Mark transaction as reconciled
   */
  async reconcile(transactionId: string, userId: string): Promise<void> {
    await updateDoc(doc(db, 'trustTransactions', transactionId), {
      reconciled: true,
      reconciledAt: serverTimestamp(),
      reconciledBy: userId,
    });
  },

  /**
   * Get unreconciled transactions
   */
  async getUnreconciled(): Promise<TrustTransaction[]> {
    const q = query(
      collection(db, 'trustTransactions'),
      where('reconciled', '==', false),
      where('deletedAt', '==', null),
      orderBy('date', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: timestampToDate(doc.data().date),
    } as TrustTransaction));
  },
};

// =============================================================================
// AUDIT SERVICE
// =============================================================================

export const auditService = {
  /**
   * Log an audit entry
   */
  async log(data: {
    entityType: AuditEntityType;
    entityId: string;
    entityName?: string;
    action: AuditAction;
    userId: string;
    userEmail?: string;
    userName?: string;
    changes?: FieldChange[];
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    const id = generateId('audit');

    const auditData: Partial<AuditLog> = {
      id,
      entityType: data.entityType,
      entityId: data.entityId,
      entityName: data.entityName,
      action: data.action,
      userId: data.userId,
      userEmail: data.userEmail || '',
      userName: data.userName || '',
      timestamp: new Date(),
      changes: data.changes,
      metadata: data.metadata,
    };

    // Add browser info if available
    if (typeof window !== 'undefined') {
      auditData.userAgent = navigator.userAgent;
    }

    await setDoc(doc(db, 'auditLogs', id), {
      ...auditData,
      timestamp: serverTimestamp(),
    });
  },

  /**
   * Get audit history for an entity
   */
  async getByEntityId(
    entityType: AuditEntityType,
    entityId: string
  ): Promise<AuditLog[]> {
    const q = query(
      collection(db, 'auditLogs'),
      where('entityType', '==', entityType),
      where('entityId', '==', entityId),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      timestamp: timestampToDate(doc.data().timestamp),
    } as AuditLog));
  },

  /**
   * Get recent activity for a user
   */
  async getByUserId(userId: string, limitCount: number = 50): Promise<AuditLog[]> {
    const q = query(
      collection(db, 'auditLogs'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      timestamp: timestampToDate(doc.data().timestamp),
    } as AuditLog));
  },

  /**
   * Get all recent activity
   */
  async getRecent(limitCount: number = 100): Promise<AuditLog[]> {
    const q = query(
      collection(db, 'auditLogs'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      timestamp: timestampToDate(doc.data().timestamp),
    } as AuditLog));
  },
};

// =============================================================================
// HELPER: Calculate field changes for audit
// =============================================================================

function calculateFieldChanges(
  oldData: Record<string, any>,
  newData: Record<string, any>
): FieldChange[] {
  const changes: FieldChange[] = [];

  const allKeys = new Set([
    ...Object.keys(oldData),
    ...Object.keys(newData),
  ]);

  for (const key of allKeys) {
    // Skip internal fields
    if (['id', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'version'].includes(key)) {
      continue;
    }

    const oldValue = oldData[key];
    const newValue = newData[key];

    // Skip if value hasn't changed
    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
      continue;
    }

    // Only track if new value is provided (not undefined)
    if (newValue !== undefined) {
      changes.push({
        field: key,
        oldValue,
        newValue,
      });
    }
  }

  return changes;
}

// =============================================================================
// EXPORT ALL SERVICES
// =============================================================================

export const practiceService = {
  clients: clientService,
  relatedParties: relatedPartyService,
  timeEntries: timeEntryService,
  expenses: expenseService,
  invoices: invoiceService,
  payments: paymentService,
  trust: trustService,
  audit: auditService,
};

export default practiceService;
